import type { RewardOdds, SpinnerOrder, SpinnerStatus, SpinReward } from '../api/types';
import type { AppState } from '../state/store';
import { versionedAssetUrl } from '../assets/version';
import { isPlayerSkinAssetId, playerSkinSpriteUrl } from '../cosmetics/visuals';
import { btn, esc, pill, row } from './components';
import { formatDuration, formatEnglishDateTime, formatRawTokenAmount, formatTokenAmount, remainingMs } from './format';
import { iconRow } from './icons';

/**
 * Spinner / Ferryman's Wager station (Phase I — prize/quote rework).
 *
 * Paid spins are quoted at $5 USD in $OBOL (Jupiter-priced server-side); each
 * quote/order expires after ~2 minutes and must be paid before then. Prizes are
 * consumable supplies (Oil Flask / Torch Bundle / Healing Draught) plus a weekly
 * spin-exclusive cosmetic on the paid wheel — never Gold, never gear. The SERVER
 * is the sole reward authority; this view never rolls or chooses a reward.
 *
 * Paying a quote also requires an app-side balance preflight: an unaffordable
 * (or unverifiable) quote disables "Pay with wallet" so Phantom is never asked
 * to sign a transaction whose simulation is known to fail.
 */

export const QUOTE_EXPIRED_MESSAGE = 'This quote expired. Create a new paid spin order.';
export const BALANCE_UNVERIFIED_MESSAGE = 'Could not verify $OBOL balance for this quote. Try again.';

const SUPPLY_META: Record<string, { label: string; asset: string }> = {
  oil: { label: 'Oil Flask', asset: '/assets/sprites/items/consumables/supply_oil_flask_01.png' },
  torch: { label: 'Torch Bundle', asset: '/assets/sprites/items/consumables/supply_torch_bundle_01.png' },
  potion: { label: 'Healing Draught', asset: '/assets/sprites/items/consumables/supply_healing_draught_01.png' },
};

function freeSpinRemainingMs(nextFreeSpinAt: string | null | undefined, nowMs = Date.now()): number {
  return remainingMs(nextFreeSpinAt ?? null, nowMs);
}

export function formatFreeSpinCountdown(nextFreeSpinAt: string | null | undefined, nowMs = Date.now()): string {
  const remainingMsValue = freeSpinRemainingMs(nextFreeSpinAt, nowMs);
  if (remainingMsValue <= 0) return 'Ready now';

  const totalSeconds = Math.ceil(remainingMsValue / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `Next free spin in ${hours}h ${minutes}m ${seconds}s`;
}

export function updateFreeSpinCountdowns(root: Pick<ParentNode, 'querySelectorAll'>, nowMs = Date.now()): void {
  const labels = root.querySelectorAll<HTMLElement>('[data-free-spin-countdown]');
  for (const label of labels) {
    const nextFreeSpinAt = label.dataset.nextFreeSpinAt ?? null;
    label.textContent = formatFreeSpinCountdown(nextFreeSpinAt, nowMs);
    if (freeSpinRemainingMs(nextFreeSpinAt, nowMs) > 0) continue;

    const card = label.closest<HTMLElement>('[data-free-spin-card]');
    // Ineligible accounts stay disabled even when the cooldown reaches zero.
    if (card?.hasAttribute('data-free-spin-ineligible')) continue;
    const button = card?.querySelector<HTMLButtonElement>('[data-free-spin-button]');
    if (!button) continue;
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.textContent = 'Take free spin';
    button.classList.remove('btn--ghost');
    button.classList.add('btn--primary');
  }
}

export function formatQuoteCountdown(expiresAt: string | null | undefined, nowMs = Date.now()): string {
  const remaining = remainingMs(expiresAt ?? null, nowMs);
  if (remaining <= 0) return QUOTE_EXPIRED_MESSAGE;
  return `Quote expires in ${formatDuration(remaining)}`;
}

/**
 * Tick the pending paid-order quote countdowns; when a quote passes its expiry
 * its card disappears from "Pending paid spins" in place (the server enforces
 * the same expiry authoritatively — this only mirrors it). When the last card
 * expires the whole section hides so an empty box never lingers.
 */
export function updateSpinnerQuoteCountdowns(root: Pick<ParentNode, 'querySelectorAll'>, nowMs = Date.now()): void {
  const labels = root.querySelectorAll<HTMLElement>('[data-spin-quote-countdown]');
  for (const label of labels) {
    const expiresAt = label.dataset.quoteExpiresAt ?? null;
    const expired = remainingMs(expiresAt, nowMs) <= 0;
    label.textContent = formatQuoteCountdown(expiresAt, nowMs);
    label.classList.toggle('spinner-quote--expired', expired);
    if (!expired) continue;
    const card = label.closest<HTMLElement>('[data-spin-order-card]');
    if (!card) continue;
    for (const button of card.querySelectorAll<HTMLButtonElement>('[data-quote-disable-on-expiry]')) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }
    card.hidden = true;
    const section = card.closest<HTMLElement>('[data-spin-pending-section]');
    if (section && [...section.querySelectorAll<HTMLElement>('[data-spin-order-card]')].every((c) => c.hidden)) {
      section.hidden = true;
    }
  }
}

/**
 * Balance preflight verdict for one pending paid order. Fail-closed: an
 * errored balance fetch blocks payment just like a confirmed shortfall.
 * An absent snapshot (still loading / different mint) leaves the button
 * enabled — the controller re-checks the balance before opening the wallet.
 */
export function paidOrderBalanceGate(
  order: SpinnerOrder,
  state: Pick<AppState, 'spinnerWalletBalance' | 'spinnerWalletBalanceError'>,
): { blocked: boolean; message: string | null } {
  const p = order.paymentInstructions;
  if (!p) return { blocked: false, message: null };
  if (state.spinnerWalletBalanceError) return { blocked: true, message: BALANCE_UNVERIFIED_MESSAGE };
  const balance = state.spinnerWalletBalance;
  if (!balance || balance.mint !== p.mint) return { blocked: false, message: null };
  try {
    if (BigInt(balance.amountRaw) < BigInt(p.totalAmount)) {
      const decimals = order.tokenDecimals ?? balance.decimals;
      return {
        blocked: true,
        message: `Insufficient $OBOL for this quote. Need ${formatRawTokenAmount(BigInt(p.totalAmount), decimals)} $OBOL, wallet has ${formatRawTokenAmount(balance.amountRaw, decimals)} $OBOL.`,
      };
    }
  } catch {
    return { blocked: true, message: BALANCE_UNVERIFIED_MESSAGE };
  }
  return { blocked: false, message: null };
}

function supplyChips(supplies: { oil: number; torch: number; potion: number }): string {
  return (['oil', 'torch', 'potion'] as const)
    .filter((key) => supplies[key] > 0)
    .map((key) => `
      <span class="supply-chip" title="${esc(SUPPLY_META[key]!.label)}">
        <img src="${esc(versionedAssetUrl(SUPPLY_META[key]!.asset))}" alt="" width="24" height="24" loading="lazy" decoding="async">
        <strong>${esc(supplies[key])}×</strong> ${esc(SUPPLY_META[key]!.label)}
      </span>`)
    .join('');
}

/**
 * Human-readable fallback for legacy (pre-rework) spin history rows. Maps old
 * reward ids where possible, otherwise a neutral "Previous reward" — never the
 * raw key or reward-table version.
 */
export function legacyRewardLabel(key: string): string {
  if (/^gold/i.test(key)) return 'Gold reward';
  const item = /^item_(common|uncommon|rare|epic|relic)/i.exec(key);
  if (item) {
    const rarity = item[1]!.toLowerCase();
    return `${rarity.charAt(0).toUpperCase()}${rarity.slice(1)} item reward`;
  }
  return 'Previous reward';
}

function lastSpinCard(reward: SpinReward | null): string {
  if (!reward) {
    return `<section class="card spinner-card spinner-card--reward spin-reward"><h3>Last spin</h3><p class="muted">No spin recorded yet.</p></section>`;
  }
  let text: string;
  if (reward.type === 'supplies' && reward.supplies) {
    // Flex line: the "Won" label and every chip share one vertical axis, and
    // overflow chips wrap as whole chips with an even gap (styles: .spin-reward__line).
    text = `<span class="spin-reward__label">Won</span><span class="spin-reward__chips">${supplyChips(reward.supplies)}</span>`;
  } else if (reward.type === 'cosmetic' && reward.cosmetic) {
    text = `<span>Won the weekly spin-exclusive cosmetic <strong>${esc(reward.cosmetic.title)}</strong>!</span>`;
  } else {
    text = `<span>${esc(legacyRewardLabel(reward.key))}</span>`;
  }
  return `<section class="card spinner-card spinner-card--reward spin-reward"><h3>Last spin</h3><p class="spin-reward__line">${text}</p></section>`;
}

function oddsTable(title: string, table: RewardOdds[]): string {
  const rows = table
    .map((o) => `<tr><td>${esc(o.detail)}</td><td>${esc(o.type === 'cosmetic' ? 'weekly cosmetic' : 'supplies')}</td><td>${esc(o.oddsPct)}%</td></tr>`)
    .join('');
  return `
    <details class="odds">
      <summary>${esc(title)} odds <span class="muted">(server-authoritative)</span></summary>
      <table class="odds-table"><thead><tr><th>Prize</th><th>Type</th><th>Chance</th></tr></thead><tbody>${rows}</tbody></table>
    </details>`;
}

function headerCard(status: SpinnerStatus): string {
  const usd = (status.paid.targetUsdCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  return `
    <section class="card spinner-card spinner-card--header market-head">
      <div class="run__head">
        <h2><span class="ember-dot" aria-hidden="true"></span> Ferryman’s Wager</h2>
        <div class="btn-row">
          ${btn('Refresh', 'spinner-refresh', {}, { variant: 'ghost' })}
          ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
        </div>
      </div>
      <p class="muted">A free spin every ${esc(status.freeSpin.cooldownHours)}h, or a paid spin for <strong>${esc(usd)} in $OBOL</strong> (live-priced, ${esc(Math.round(status.paid.quoteTtlSeconds / 60))}-minute quote). Paid spins are a pure token sink: <strong>50% burned</strong> / <strong>50% treasury</strong>, verified on-chain before the wheel turns. Prizes are run supplies and a weekly spin-exclusive cosmetic — never Gold, never gear.</p>
      ${iconRow('Your Gold', 'gold', `<strong>${esc(status.goldBalance)}</strong>`)}
    </section>`;
}

export const FREE_SPIN_ELIGIBILITY_MESSAGE = 'Requires active $OBOL eligibility. Re-check eligibility first.';

function freeSpinCard(status: SpinnerStatus): string {
  const f = status.freeSpin;
  // Backend-authoritative hold-to-play gate: the server also rejects the spin,
  // this only mirrors it so players see why the button is unavailable.
  if (f.eligible === false) {
    return `
      <section class="card spinner-card spinner-card--spin spinner-card--free" data-free-spin-card data-free-spin-ineligible>
        <h2>Free spin</h2>
        <div class="btn-row">
          ${btn('Free spin unavailable', 'spinner-free', { 'free-spin-button': '1' }, { variant: 'ghost', disabled: true })}
          ${btn('Re-check eligibility', 'refresh-eligibility', {}, { variant: 'primary' })}
        </div>
        <p class="hint">${esc(FREE_SPIN_ELIGIBILITY_MESSAGE)}</p>
        ${oddsTable('Free spin', status.freeRewardTable)}
      </section>`;
  }
  const ready = f.available || freeSpinRemainingMs(f.nextFreeSpinAt) <= 0;
  const cta = f.available || ready
    ? btn('Take free spin', 'spinner-free', { 'free-spin-button': '1' }, { variant: 'primary' })
    : `${btn('Free spin on cooldown', 'spinner-free', { 'free-spin-button': '1' }, { variant: 'ghost', disabled: true })}<span class="muted spinner-countdown" data-free-spin-countdown data-next-free-spin-at="${esc(f.nextFreeSpinAt ?? '')}">${esc(formatFreeSpinCountdown(f.nextFreeSpinAt))}</span>`;
  return `
    <section class="card spinner-card spinner-card--spin spinner-card--free" data-free-spin-card>
      <h2>Free spin</h2>
      <p class="muted">Free spins award supplies only.</p>
      <div class="btn-row">${cta}</div>
      ${oddsTable('Free spin', status.freeRewardTable)}
    </section>`;
}

function weeklyCosmeticCard(status: SpinnerStatus): string {
  const weekly = status.weeklyCosmetic;
  if (!weekly) return '';
  const meta = weekly.metadata && typeof weekly.metadata === 'object' ? (weekly.metadata as { appearanceAssetId?: unknown }) : {};
  const assetId = isPlayerSkinAssetId(meta.appearanceAssetId) ? meta.appearanceAssetId : null;
  const spriteUrl = playerSkinSpriteUrl(assetId);
  const preview = assetId && spriteUrl
    ? `
      <figure class="skin-preview skin-preview--animated weekly-cosmetic__preview" data-skin-preview="animated" data-appearance-asset-id="${esc(assetId)}" aria-label="${esc(weekly.title)} idle skin preview">
        <span class="skin-preview__stage" aria-hidden="true">
          <span class="skin-preview__sprite" style="--skin-sheet: url('${esc(versionedAssetUrl(spriteUrl))}');"></span>
        </span>
      </figure>`
    : '';
  return `
    <section class="card spinner-card spinner-card--weekly">
      <span class="weekly-cosmetic__ribbon" aria-hidden="true">Weekly exclusive</span>
      <h2>Weekly spin-exclusive cosmetic</h2>
      <div class="weekly-cosmetic">
        ${preview}
        <div class="weekly-cosmetic__details">
          <p class="weekly-cosmetic__name"><strong>${esc(weekly.title)}</strong></p>
          <p class="weekly-cosmetic__tags">${pill('paid spins only', 'warn')} ${pill('tradeable', 'muted')} ${pill('weekly exclusive', 'ok')}</p>
          <p class="muted">Won only from paid spins while this weekly cosmetic is active. Copies won are yours forever; after the window closes it can no longer be newly won.</p>
          <p class="weekly-cosmetic__until">Available until <span data-spin-quote-static title="${esc(weekly.endsAt)}">${esc(formatEnglishDateTime(weekly.endsAt))}</span></p>
        </div>
      </div>
    </section>`;
}

function paidSpinCard(status: SpinnerStatus, paymentBusy: boolean): string {
  const p = status.paid;
  const usd = (p.targetUsdCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  if (!p.available) {
    return `
      <section class="card spinner-card spinner-card--spin">
        <h2>Paid spin</h2>
        ${row('Price', `<strong>${esc(usd)}</strong> in $OBOL`)}
        <p class="hint spinner-unavailable" role="status">${esc(p.unavailableReason ?? 'Paid spinner temporarily unavailable.')}</p>
        <div class="btn-row">${btn('Paid spin unavailable', 'spinner-create', {}, { variant: 'ghost', disabled: true })}</div>
        ${oddsTable('Paid spin', status.paidRewardTable)}
      </section>`;
  }
  const estimate = p.estimatedTotalAmount !== null
    ? `≈ <strong>${esc(formatTokenAmount(p.estimatedTotalAmount, p.tokenDecimals))}</strong> $OBOL right now`
    : '<span class="muted">quoted at order time</span>';
  return `
    <section class="card spinner-card spinner-card--spin">
      <h2>Paid spin</h2>
      ${row('Price', `<strong>${esc(usd)}</strong> in $OBOL — ${estimate}`)}
      ${row('Split', `burn ${esc(p.splitBps.burn / 100)}% · treasury ${esc(p.splitBps.treasury / 100)}%`)}
      ${row('Quote window', `${esc(Math.round(p.quoteTtlSeconds / 60))} minutes — pay before the quote expires`)}
      <div class="btn-row">${btn(`Create paid spin order (${usd} in $OBOL)`, 'spinner-create', {}, { variant: 'primary', disabled: paymentBusy })}</div>
      ${oddsTable('Paid spin', status.paidRewardTable)}
    </section>`;
}

function instructions(order: SpinnerOrder, state: AppState, paymentBusy: boolean, nowMs: number): string {
  const p = order.paymentInstructions;
  if (!p) return '';
  const expired = remainingMs(p.expiresAt, nowMs) <= 0;
  const gate = paidOrderBalanceGate(order, state);
  const mock = p.devMockSignature
    ? `${btn('Submit mock tx', 'spinner-submit-mock', { order: order.id, tx: p.devMockSignature, 'quote-disable-on-expiry': '1' }, { variant: 'ghost', disabled: expired })}<code>${esc(p.devMockSignature)}</code>`
    : '';
  const gateNote = gate.blocked && gate.message
    ? `<p class="hint spinner-balance-gate" role="status">${esc(gate.message)}</p>`
    : '';
  return `
    <div class="cashout-instructions payment-instructions" aria-label="Payment instructions">
      ${row('Amount', `<strong>${esc(formatTokenAmount(p.totalAmount, order.tokenDecimals))} $OBOL</strong> <span class="muted">(burn ${esc(formatTokenAmount(p.burnAmount, order.tokenDecimals))} / treasury ${esc(formatTokenAmount(p.treasuryAmount, order.tokenDecimals))})</span>`)}
      ${gateNote}
      <div class="btn-row">
        ${btn('Pay with wallet', 'spinner-pay', { order: order.id, 'quote-disable-on-expiry': '1' }, { variant: 'primary', disabled: paymentBusy || expired || gate.blocked })}
        <input class="market-price cashout-tx" type="text" data-spin-tx-for="${esc(order.id)}" placeholder="Solana tx signature" aria-label="Solana tx signature" />
        ${btn('Submit tx & spin', 'spinner-submit', { order: order.id, 'quote-disable-on-expiry': '1' }, { variant: 'ghost', disabled: expired })}
        ${mock}
        ${btn('Cancel', 'spinner-cancel', { order: order.id }, { variant: 'ghost' })}
      </div>
    </div>`;
}

function pendingOrderCard(order: SpinnerOrder, state: AppState, paymentBusy: boolean, nowMs: number): string {
  const expired = remainingMs(order.expiresAt, nowMs) <= 0;
  const usd = order.targetUsdCents !== null ? `$${(order.targetUsdCents / 100).toFixed(2)}` : null;
  const title = usd
    ? `<strong>${esc(formatTokenAmount(order.priceTotal, order.tokenDecimals))} $OBOL</strong> <span class="muted">(${esc(usd)} quote)</span>`
    : `<strong>${esc(order.priceTotal)} $OBOL</strong>`;
  return `
    <div class="item" data-spin-order-card>
      <div class="item__head">${title} ${expired ? pill('expired', 'down') : pill('pending', 'warn')}</div>
      <div class="item__meta">
        <span class="spinner-quote-countdown${expired ? ' spinner-quote--expired' : ''}" data-spin-quote-countdown data-quote-expires-at="${esc(order.expiresAt)}" role="timer">${esc(formatQuoteCountdown(order.expiresAt, nowMs))}</span>
      </div>
      ${instructions(order, state, paymentBusy, nowMs)}
    </div>`;
}

/** Pending = payable. Expired orders stay in DB history but never render here. */
export function payablePendingOrders(orders: readonly SpinnerOrder[], nowMs: number): SpinnerOrder[] {
  return orders.filter((o) => remainingMs(o.expiresAt, nowMs) > 0);
}

/** Rendered only when pending orders exist so an empty box never dominates the page. */
function pendingOrdersCard(status: SpinnerStatus, state: AppState, paymentBusy: boolean, nowMs: number): string {
  const orders = payablePendingOrders(status.pendingOrders ?? [], nowMs);
  if (orders.length === 0) return '';
  const cards = orders.map((o) => pendingOrderCard(o, state, paymentBusy, nowMs)).join('');
  return `<section class="card spinner-card spinner-card--pending" data-spin-pending-section><h2>Pending paid spins</h2><div class="items">${cards}</div></section>`;
}

export function renderSpinner(state: AppState, nowMs = Date.now()): string {
  if (!state.session) {
    return `<section class="card"><h2>Ferryman’s Wager</h2><p class="muted">Connect a wallet to spin.</p></section>`;
  }
  const status = state.spinner;
  if (!status) {
    return `<section class="card"><h2>Ferryman’s Wager</h2><p class="muted">Loading the wheel…</p></section>`;
  }
  const paymentBusy = state.busy === 'paying with wallet & spinning' || state.busy === 'creating a paid spin order';
  const lastReward = state.lastSpinReward ?? status.lastSpin?.reward ?? null;
  return `
    <div class="spinner-layout">
      ${headerCard(status)}
      <div class="spinner-col">
        ${freeSpinCard(status)}
        ${lastSpinCard(lastReward)}
      </div>
      <div class="spinner-col">
        ${paidSpinCard(status, paymentBusy)}
        ${pendingOrdersCard(status, state, paymentBusy, nowMs)}
        ${weeklyCosmeticCard(status)}
      </div>
    </div>`;
}
